<template>
  <div class="wrap-container search-component-wrap">
    <div
      class="search-component"
    >
      <div
        ref="searchWrapper"
        class="search-wrapper"
        @keydown.arrow-up.prevent="prevFocus"
        @keydown.arrow-down.prevent="nextFocus"
        @keyup.escape="blur"
      >
        <div class="initial-search-wrapper">
          <div
            :class="{mobileMode : mobileMode}"
            class="search-input-wrapper"
          >
            <label
              :class="{mobileMode : mobileMode}"
              class="search-input-label"
              for="ica-search-input"
            >
              <input
                id="ica-search-input"
                ref="searchInputRef"
                :value="searchInput"
                :class="{mobileMode : mobileMode}"
                class="font-text-3--regular"
                placeholder="Sök t.ex recept eller butik"
                type="search"
                aria-label="Sök t.ex recept eller butik"
                autocapitalize="off"
                autocomplete="off"
                @search="onSearchEvent"
                @input="onSearch"
                @focus="toggleQuickLinks"
                @keyup.enter="goToSearchResPage(searchResults, searchInput, $event)"
              >
              <svgicon
                v-if="!mobileMode"
                :class="{ hidden : searchInput !== '' }"
                :name="search"
                class="search-icon"
              />
              <button
                v-if="searchInput !== ''"
                type="button"
                aria-label="Rensa sökfrågan"
                :class="{ active : searchInput !== '' }"
                class="clear-search-button no-keyboard-navigation"
                @click="wipeSearch"
              >
                <svgicon
                  :name="close"
                  class="close-icon"
                />
              </button>
              <span class="search-input-line" />
            </label>
            <button
              type="button"
              class="button--transparent button--40 cancel-search no-keyboard-navigation"
              @click="resetSearch"
            >
              Avbryt
            </button>
          </div>
          <div
            v-if="showQuickLinks && quickLinksContent.length"
            :class="{show : showQuickLinks}"
            class="quick-links slide-down"
          >
            <header class="font-text-1--black search-category search-result-padding">
              Snabblänkar
            </header>
            <a
              v-for="(quickLink, i) in quickLinksContent"
              :key="i"
              :style="{ animationDelay: ( i * (1 / 18)) + 's' }"
              :href="quickLink.Url"
              class="search-result-padding animate fadeInRight js-track-search-links"
              @click="trackQuickLinks(quickLink.Url, quickLink.Title)"
            >
              <svgicon :name="quickLink.Name" />
              <div class="quick-links-texts">
                <h2 class="font-rubrik-2--light">{{ quickLink.Title }} </h2>
              </div>
            </a>
          </div>
        </div>
        <div
          ref="searchResultsWrapper"
          class="search-results-wrapper"
        >
          <div
            v-if="showSearchResults && active"
            :class="{
              show: showDropDown,
              'has-button': hasResultItems
            }"
            class="search-results slide-down"
          >
            <div
              v-if="hasResultItems"
              class="number-of-searches search-result-padding"
            >
              <p class="font-text-1--regular">
                <span class="font-text-1--black">{{ searchResults.totalHits }}</span> resultat för: {{ searchInput }}
              </p>
            </div>
            <errorPage
              v-if="!hasResultItems"
              :class="{show : !hasResultItems}"
              :search-input="searchInput"
              :view-past-searches="viewPastSearches"
              :error-header="notFoundHeader"
              :error-preamble="notFoundPreamble"
              :is-error-type="'notFoundPage'"
              class="search-results slide-down"
              @gotolatestsearch="goToLatestSearch"
              @clearlocalstorage="clearLocalStorage"
            />
            <template v-for="(item, i) in resultsArr">
              <article
                v-if="hasResultItems && item.Documents.length"
                :key="item.SectionName"
                :value="item.SectionName"
                class="result-categories animate fadeInRight"
              >
                <header
                  v-if="item.Documents.length > 0"
                  class="font-text-1--black search-category sticky-header search-result-padding"
                >
                  {{ translate(item.SectionName) }} ( {{ item.Stats.TotalHits }} )
                </header>
                <searchResultComponent
                  :section-name="item.SectionName"
                  :search-result="searchResults"
                  :highlight-item="item.HighlightItem"
                  :documents="item.Documents"
                  :search-input="searchInput"
                  :position="i"
                />
                <goToSection
                  :section-name="item.SectionName"
                  :search-input="searchInput"
                  :initial-hits="currentHits"
                  :documents="item.Documents"
                  :total-hits="item.Stats.TotalHits"
                />
              </article>
            </template>
          </div>
          <div
            v-if="!mobileMode && showDropDown && hasResultItems && showSearchResults"
            class="search-button-wrapper"
          >
            <a
              :href="searchResults.searchUrl"
              class="button--secondary button--40"
              @click="goToSearchResPage(searchResults, searchInput, $event)"
            >
              Visa alla resultat ({{ searchResults.totalHits }})
            </a>
          </div>
          <errorPage
            v-if="errorView"
            :class="{show: errorView}"
            :search-input="searchInput"
            :view-past-searches="viewPastSearches"
            :error-header="errorHeader"
            :error-preamble="errorMessage ? errorMessage : errorPreamble"
            :is-error-type="'errorPage'"
            class="search-results slide-down"
            @gotolatestsearch="goToLatestSearch"
            @clearlocalstorage="clearLocalStorage"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import debounce from 'lodash.debounce'
import goToSection from "./goToSection"
import Stickyfill from "@/utils/stickyfill.es6.js"
import ga from '@/analytics/dataLayerSearch.js'
import searchResultComponent from "./resultComponent"
import errorPage from "./errorPages"
import '#/Navigation/Search'
import '#/Controls/Close'
import '#/Shop/ShoppingBag'
import '#/Shop/Bonus'
import '#/Shop/AccountBalance'
import '#/Shop/Offer'

const translations = {
  ArticleResult: 'Artiklar',
  FoodSchoolResult: 'Matskolor',
  RecipeCategoryResult: 'Receptsamlingar',
  RecipeResult: 'Recept',
  ServicesResult: 'Tjänster',
  StoreResult: 'Butiker',
  ProductResult: 'Produkter'
}

export default {
  name: "SearchComponent",
  components: {
    goToSection,
    searchResultComponent,
    errorPage
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      search: 'Navigation/Search',
      close: 'Controls/Close',
      grocerybag: 'Shop/ShoppingBag',
      bonus: 'Shop/Bonus',
      balance: 'Shop/AccountBalance',
      searchResults: null,
      searchInput: '',
      showDropDown: false,
      showQuickLinks: false,
      errorView: false,
      viewPastSearches: [],
      notFoundHeader: 'Inga resultat',
      notFoundPreamble: 'Kontrollera att orden är rättstavade eller testa med andra sökord',
      errorHeader: 'Oops, tekniskt fel',
      errorPreamble: 'Vi upplever just nu tekniska problem. Vänligen testa att söka igen!',
      errorMessage: null,
      showLatestSearch: false,
      quickLinksContent: [],
      currentHits: 5,
    }
  },
  computed: {
    showSearchResults () {
      if(this.searchResults && !this.showQuickLinks) {
        return true
      }
      return false
    },
    hasResultItems () {
      return this.searchResults && this.searchResults.hasResultItems
    },
    resultsArr () {
      const { result } = this.searchResults
      const resultsSectionList = ['ArticleResult', 'RecipeCategoryResult', 'RecipeResult', 'ServicesResult', 'StoreResult', 'ProductResult', 'FoodSchoolResult']
      return Object.keys(result)
      .filter( key => resultsSectionList.includes(key))
      .map(item => ({
        SectionName: item,
        Documents: result[item].Documents,
        HighlightItem: result[item].HighlightItem,
        Stats: result[item].Stats
      }))
    },
    mobileMode () {
      return this.$root.currentSizeLte('md')
    }
  },
  watch: {
    active () {
      if (this.active === true) {
        window.addEventListener('click',this.onDesktopClick)

        this.$nextTick()
          .then(() => {
            this.$refs.searchInputRef.focus()
            this.showDropDown = true
        })
      } else {
        window.removeEventListener('click', this.onDesktopClick)
      }
    }
  },
  mounted () {
    let stickyHeaders = document.querySelectorAll('.sticky-header')
    Stickyfill.add(stickyHeaders)
  },
  destroyed () {
    window.removeEventListener('click', this.onDesktopClick)
  },
  methods: {
    onSearchEvent () {
      if (this.mobileMode) {
        this.$refs.searchInputRef.blur()
      }
    },
    moveFocus (indexChange) {
      if (this.$refs.searchWrapper) {
        const links = this.$refs.searchWrapper.querySelectorAll('#ica-search-input, a, button:not(.no-keyboard-navigation)')
        const currentFocus = this.$refs.searchWrapper.querySelector('#ica-search-input:focus, a:focus, button:not(.no-keyboard-navigation):focus')

        const currentFocusIndex = currentFocus ? Array.from(links).indexOf(currentFocus) : -1
        let nextFocusIndex = currentFocusIndex + indexChange
        if (nextFocusIndex < 0) {
          nextFocusIndex = links.length - 1
        } else if (nextFocusIndex >= links.length) {
          nextFocusIndex = 0
        }
        const nextFocusElement = links[nextFocusIndex]
        if (nextFocusElement) {
          nextFocusElement.focus()
        }
      }
    },
    prevFocus () {
      this.moveFocus(-1)
    },
    nextFocus () {
      this.moveFocus(1)
    },
    blur (e) {
      e.target.blur()
      this.resetSearch()
    },
    toggleQuickLinks () {
      this.$emit("searchfocus")
      if (!this.searchResults && this.errorView) {
        this.showQuickLinks = false
      } else if ((!this.searchResults && !this.errorView) || (this.hasResultItems && this.searchInput === '')) {
        !this.quickLinksContent.length && this.loadQuickLinksPromise()
      } else {
        this.showQuickLinks = false
      }
    },
    async loadQuickLinksPromise () {
      try {
        const response = await fetch(`${this.$root.envConfig.baseUrl}/api/search/v2/quicklinks`)
        const res = await response.json()
        if (response.ok) {
          this.quickLinksContent = res ? res.Documents : this.quickLinksContent
          this.showQuickLinks = res.Documents ? true : false
          return
        }

        const errorMessage = res.ExceptionMessage ? res.ExceptionMessage : res.message
        throw new Error(errorMessage)
      } catch (err) {
          this.errorMessage = err.message.includes('http') ? `${this.errorPreamble} <br> Alternativt gå in via länken nedan för att hitta dina favorit recept. <br> <a href=${err.message}>Hitta mina recept</a>` : `${this.errorPreamble}`
          this.errorView = true
          this.showQuickLinks = false
      }
    },
    onDesktopClick (e) {
      const target = e.target
      const linkClick = target.tagName === 'A'
      let searchWrapperClick = true


      if (!this.mobileMode) {
        searchWrapperClick = Array.from(this.$refs.searchWrapper.getElementsByTagName(target.tagName)).includes(target)
      }

      if(!searchWrapperClick || linkClick) {
        this.showDropDown = false
        this.showQuickLinks = false
        this.errorView = false
        this.$refs.searchInputRef.blur()
        this.$emit("cancelsearchbar")
        return
      }
      this.showDropDown = true
    },
    onSearch (e) {
      this.searchInput = e ? e.target.value : this.searchInput
      if (e || this.showLatestSearch ) {
        let query = this.searchInput
        this.showQuickLinks = query !== '' ? false : true
        if(query.length >= 2) {
          this.doDebouncedSearch()
        } else {
          this.searchResults = null
        }
      }
    },
    saveSearchResults: debounce(function (query) {
      let pastSearches = this.viewPastSearches

      if (localStorage["pastSearches"]) {
        pastSearches = JSON.parse(localStorage["pastSearches"])
      }

      if (!pastSearches.includes(query)) {
        pastSearches.unshift(query)
        if (pastSearches.length > 5) {
          pastSearches.pop()
        }
        localStorage["pastSearches"] = JSON.stringify(pastSearches)
      }

      if (pastSearches.length) {
        this.viewPastSearches = pastSearches

        this.viewPastSearches = this.viewPastSearches.map(searchItem =>
          searchItem.charAt(0).toUpperCase() + searchItem.substr(1)
        )
        return this.viewPastSearches
      }
      return ''
    }, 1500),
    clearLocalStorage () {
      this.viewPastSearches = []
      this.showLatestSearch = true
      localStorage.clear()
    },
    doDebouncedSearch: debounce(
      function () {
        const input = this.searchInput
        if(input.length < 2) {
          this.searchResults = null
          this.showQuickLinks = true
          this.errorView = false
          return
        }

        fetch(`${this.$root.envConfig.baseUrl}/api/search/v2/quicksearch?query=${encodeURIComponent(input)}&take=${this.currentHits}`)
          .then(res => {
            if (input !== this.searchInput) {
              throw new Error('aborted')
            }

            if(res.ok) {
              this.showDropDown = true
              return res.json()
            }
          })
          .then(res => {
            this.searchResults = {
              hasResultItems: res.HasResultItems,
              totalHits: res.TotalHits,
              searchUrl: res.SearchUrl,
              result: this.sortSearchResults({
                ...Object.keys(res).reduce((acc, key) => {
                  if (key) {
                    acc[key] = res[key]
                  }
                  return acc
                }, {})
              })
            }
          })
          .then(() => {
            if (this.hasResultItems) {
              this.errorView = false
              if (!this.viewPastSearches.includes(input)) {
                ga.debounceSearchInputRes(this.searchResults, input)
              }
              this.saveSearchResults(input)
            } else {
              ga.searchInputNoRes(this.searchResults, input)
            }
          })
          .catch((err) => {
            if (err.message !== 'aborted') {
              this.searchResults = null
              this.errorView = true
              this.showQuickLinks = false
            }
          })
      }
    , 300),
    sortSearchResults (results) {
      const ordered = ['ServicesResult', 'ProductResult', 'RecipeResult', 'RecipeCategoryResult', 'ArticleResult', 'FoodSchoolResult', 'StoreResult']
      return Object.keys(results)
      .sort((a,b) => ordered.indexOf(a) - ordered.indexOf(b))
      .reduce((acc, curr) => (acc[curr] = results[curr], acc), {})
    },
    resetSearch () {
      this.searchInput = ''
      this.searchResults = null
      this.showQuickLinks = false
      this.$emit("cancelsearchbar")
    },
    wipeSearch (e) {
      e.preventDefault()
      e.stopPropagation()
      this.searchInput = ''
      this.searchResults = null
      this.showQuickLinks = true
      this.errorView = false
      let self = this
      this.$nextTick()
        .then(function () {
          self.$refs.searchInputRef.focus()
      })
    },
    translate (text) {
      return translations[text] || text
    },
    checkImgUrl (url) {
      return url.includes('ica.se') ? url : `https://www.ica.se/${url}`
    },
    goToSearchResPage (searchResults, searchInput) {
      if (!searchInput) {
        return
      }
      // Send to analytics
      if (searchResults) {
        ga.showAllResultsClick(searchResults, searchInput)
      }

      if (!this.mobileMode) {
        const url = encodeURIComponent(this.searchInput)
        location.href = `/sok/#:search=${url}`
      }

      this.$refs.searchInputRef.blur()
    },
    goToLatestSearch (value) {
      this.searchInput = value
      this.showLatestSearch = true
      this.onSearch()
    },
    trackQuickLinks (url, name) {
      ga.quickLinksTracking(url, name)
      this.resetSearch()
    }
  }
}

</script>

<style lang="scss">

.wrap-container.search-component-wrap {
  padding: 0.75rem 0.5rem 0.75rem 0;
  position: relative;
  z-index: 2;
  background-color: $color-white;
  width: auto;

  @include media-breakpoint-up(lg) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

.search-component {
  /* Temporary styles, remove after testing */
  h1,
  h2,
  h3,
  h4,
  p {
    margin-top: 0;
    margin-bottom: 0;
  }

  .search-result-padding {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .quick-links {
    display: none;

    &.show {
      display: block;

      header {
        padding-top: 1rem;
        border-top: 0.0625rem solid $color-grey-special;
      }
    }

    a {
      margin: 0 1rem;
      padding: 1rem 0;
      text-decoration: none;
      display: flex;
      justify-content: left;
      align-items: center;
      color: $color-grey-mid;
      fill: $color-secondary-cta-text;

      &:hover {
        color: $color-pink-light;
        fill: $color-pink-light;
      }

      &:active {
        color: $color-red-dark;
        fill: $color-red-dark;
      }

      &:not(:last-child) {
        border-bottom: 0.0625rem solid $color-grey-special;
      }

      svg {
        width: 3.75rem;
        height: 3.75rem;
      }

      .quick-links-texts {
        margin-left: 0.5rem;
        width: 31.25rem;
      }

      h2 {
        margin: 0;
      }

      p {
        margin: 0;
        color: $color-black;
      }
    }
  }

  .search-results-wrapper,
  .quick-links {
    position: absolute;
    background: $color-white;
    margin-top: 0.75rem;
    width: 100%;

    @include media-breakpoint-up(lg) {
      box-shadow: 0 0.3125rem 1.25rem -0.3125rem rgba(14, 14, 14, 0.41);
    }

    @keyframes fadeInRight {
      from {
        opacity: 0;
        transform: translate3d(10%, 0, 0);
      }

      to {
        opacity: 1;
        transform: none;
      }
    }

    .fadeInRight {
      animation-name: fadeInRight;
      animation-duration: 0.6s;
      animation-fill-mode: both;
    }

    article.fadeInRight {
      animation-delay: 0.2s;
    }
  }

  .search-results {
    display: none;
    max-height: calc(100vh - 4.5rem);
    position: relative;
    height: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 6.25rem;

    @include media-breakpoint-up(md) {
      padding-bottom: 0;
    }

    @include media-breakpoint-up(lg) {
      max-height: calc(70vh - 3.75rem);
    }

    .top-nav.search-active & {
      display: block;
      background: $color-white;

      @include media-breakpoint-up(lg) {
        box-shadow: 0 0.3125rem 1.25rem -0.3125rem rgba(14, 14, 14, 0.41);
      }
    }

    &.has-button {
      @include media-breakpoint-up(lg) {
        margin-bottom: 3.75rem;
      }
    }

    .number-of-searches {
      background-color: $color-grey-lightest;
      height: 2rem;
      position: relative;
      border-top: 0.0625rem solid $color-grey-special;

      @include media-breakpoint-up(lg) {
        border-top: none;
      }

      p {
        position: absolute;
        top: 50%;
        transform: translate(0, -50%);
      }
    }

    .result-categories {
      margin: 0.5rem 0;

      .sticky-header {
        position: sticky;
        top: 0;
        left: 0;
        background-color: $color-white;
        z-index: 99;
        padding-top: 0.5rem;
      }

      &:not(:nth-last-child(1)) {
        border-bottom: 0.25rem solid $color-grey-special;
      }

      &__item {
        a.category-item-box {
          color: $color-black;
          text-decoration: none;
          max-width: 100%;
          padding: 1rem 0;
          margin: 0 1rem;
          border-top: 0.0625rem solid $color-grey-special;
          display: block;

          figure {
            margin: 0;
            display: flex;
            align-items: center;

            a {
              border-bottom: none;
              text-decoration: none;
            }

            .shop-online-btn {
              margin-top: 0.25rem;
            }
          }

          .highlighted-query {
            background-color: $color-purple-special;
          }
        }

        a.category-item-box:nth-of-type(1) {
          border-top: none;
          animation: none;
        }

        &:nth-last-of-type(1) a.category-item-box {
          padding-bottom: 1rem;
        }

        img {
          margin-right: 1.25rem;
          min-width: 6.5rem;
          max-width: 6.5rem;
          height: 6.5rem;
          border-radius: 0.125rem 0.125rem 0.125rem 0.125rem;
          display: block;
          object-fit: contain;

          &.smaller-img {
            min-width: 4.5rem;
            max-width: 4.5rem;
            height: 4.5rem;
          }
        }

        .store-img {
          width: 4.5rem;
          height: 4.5rem;
          margin-right: 1.25rem;
        }

        &.not-found-page {
          padding: 1rem 0;
          cursor: pointer;
          margin: 0;

          &:first-child {
            padding-top: 1.5rem;
          }

          &:not(:last-child) {
            border-bottom: 0.0625rem solid $color-grey-special;
          }

          svg {
            fill: $color-secondary-cta-text;
            width: 1.25rem;
            height: 1.25rem;
          }
        }
      }

      &-header-container {
        display: flex;
        justify-content: space-between;
      }
    }

    footer {
      text-align: center;
      margin: 0 1rem 0.5rem;
      border-top: 0.0625rem solid $color-grey-special;
      padding-top: 0.5rem;
      position: relative;
    }
  }

  .search-button-wrapper {
    background-color: $color-white;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 0.625rem 0;
    height: 3.75rem;
    position: absolute;
    border-top: 0.125rem solid $color-grey-special;
    text-align: center;

    .button {
      font-weight: bold;
      background: $color-primary-cta;
      border: 0;
      border-radius: 5rem;
      color: $color-white;
      cursor: pointer;
      display: inline-block;
      min-width: 18rem;
      padding: 0.7rem 2rem 0.7rem;
      text-align: center;
      width: 100%;
      text-decoration: none;
    }
  }

  .search-wrapper {
    position: relative;

    .slide-down {
      -ms-overflow-y: auto;
      -ms-overflow-x: hidden;
      overflow-y: auto;
      overflow-x: hidden;

      &.search-results {
        animation: scroll-down-search-results-mobile ease 0.5s forwards;

        @include media-breakpoint-up(lg) {
          animation: scroll-down-search-results ease 0.5s forwards;
        }
      }

      &.quick-links {
        animation: scroll-down-quick-links ease 0.5s forwards;
      }
    }

    @keyframes scroll-down-search-results-mobile {
      from {
        max-height: 0;
      }

      to {
        max-height: calc(100vh - 4.5rem);
      }
    }

    @keyframes scroll-down-search-results {
      from {
        max-height: 0;
      }

      to {
        max-height: calc(70vh - 3.75rem);
      }
    }

    @keyframes scroll-down-quick-links {
      from {
        max-height: 0;
      }

      to {
        max-height: 70vh;
      }
    }

    .search-category {
      text-transform: uppercase;
      color: $color-inactive-text;
      padding-bottom: 0.5rem;

      &.clear {
        color: $color-secondary-cta-text;
        cursor: pointer;
      }
    }

    .initial-search-wrapper {
      position: relative;

      .search-input-wrapper {
        display: flex;
        position: relative;
        margin: 0 1rem;

        @include media-breakpoint-up(lg) {
          margin: 0;
        }

        .top-nav.search-active & {
          animation-duration: 1.6s;
          animation-fill-mode: both;
          animation-name: fadeInSearch;
          animation-delay: 0.1s;

          @keyframes fadeInSearch {
            from {
              opacity: 0;
            }

            to {
              opacity: 1;
            }
          }

          @include media-breakpoint-up(lg) {
            animation: none;
          }
        }
      }

      .search-input-label {
        position: relative;
        display: block;
        height: 2.5rem;
        border-radius: 0.25rem;
        overflow: hidden;
      }

      button.cancel-search {
        display: block;
        transition: opacity 0.2s 0s ease-in-out;
        opacity: 0;
        overflow: unset;

        @include media-breakpoint-up(lg) {
          display: none;
        }
      }

      .search-input-line {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        margin: 0 auto;
        width: 0;
        height: 0.125rem;
        background: $color-red;
        transition: 0.5s ease-in-out;
      }

      label {
        width: 100%;
        transition: width 0.5s ease-in-out;

        &.mobileMode {
          ~ button.cancel-search {
            transition-delay: 0.2s;
            opacity: 1;
          }
        }
      }

      input {
        border: 0.0625rem solid $color-grey-light;
        border-radius: 0.25rem;
        color: $color-black;
        box-shadow: none;
        font-weight: normal;
        height: 2.5rem;
        width: 100%;
        padding: 0.5rem 2rem 0.5rem 1rem;
        position: absolute;
        z-index: 0;
        background: none;
        top: 0;
        left: 0;
        transition: width 0.5s ease-in-out;
        appearance: none;

        &::-webkit-search-decoration,
        &::-webkit-search-cancel-button,
        &::-webkit-search-results-button,
        &::-webkit-search-results-decoration {
          appearance: none;
        }

        &::-ms-clear {
          display: none;
        }

        &:focus,
        &.mobileMode {
          outline: none;
        }

        &:focus ~ .search-input-line {
          width: 100%;
        }
      }

      @ica-legacy {
        input {
          box-sizing: border-box;
        }
      }

      .clear-search-button,
      .search-icon {
        height: 1.25rem;
        width: 1.25rem;
        margin-top: -0.625rem;
        position: absolute;
        z-index: 5;
        top: 50%;
      }

      .search-icon {
        fill: $color-grey-mid;
        right: 0.625rem;
        opacity: 1;
        transition: opacity 0.25s ease-in-out;

        &.hidden {
          opacity: 0;
        }
      }

      .clear-search-button {
        right: 0.625rem;
        z-index: 10;
        background: none;
        border: none;
        padding: 0;
        box-shadow: none;
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.5s ease-in-out;
        pointer-events: none;

        .close-icon {
          fill: $color-secondary-cta-text;

          &:hover {
            fill: $color-pink-light;
          }
        }

        &.active {
          display: block;
          opacity: 1;
          pointer-events: auto;
        }
      }
    }
  }
}
</style>
